import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '';

if (!BACKEND_URL) {
  console.error('REACT_APP_BACKEND_URL is not set! API calls will fail.');
}

// Create axios instance with credentials
const api = axios.create({
  baseURL: API || '/api', // Fallback to relative path if not set
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to handle missing backend URL
api.interceptors.request.use(
  (config) => {
    if (!BACKEND_URL) {
      console.error('Backend URL not configured. Request will likely fail:', config.url);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to log errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  exchangeSession: async (sessionId) => {
    const response = await api.get('/auth/session', {
      headers: { 'X-Session-ID': sessionId }
    });
    return response.data;
  },
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  }
};

// Agents API
export const agentsAPI = {
  list: async () => {
    const response = await api.get('/agents');
    return response.data;
  },
  get: async (agentId) => {
    const response = await api.get(`/agents/${agentId}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/agents', data);
    return response.data;
  },
  update: async (agentId, data) => {
    const response = await api.patch(`/agents/${agentId}`, data);
    return response.data;
  },
  delete: async (agentId) => {
    const response = await api.delete(`/agents/${agentId}`);
    return response.data;
  },
  getDomains: async (agentId) => {
    // Prompt 10: Get allowed domains for agent
    const response = await api.get(`/agents/${agentId}/domains`);
    return response.data;
  },
  addDomain: async (agentId, domain) => {
    // Prompt 10: Add allowed domain
    const response = await api.post(`/agents/${agentId}/domains`, { domain });
    return response.data;
  },
  deleteDomain: async (agentId, domainId) => {
    // Prompt 10: Delete allowed domain
    const response = await api.delete(`/agents/${agentId}/domains/${domainId}`);
    return response.data;
  }
};

// Knowledge API
export const knowledgeAPI = {
  list: async (agentId) => {
    const response = await api.get(`/knowledge/${agentId}`);
    return response.data;
  },
  listSources: async (agentId) => {
    // Prompt 7: List knowledge sources
    const response = await api.get(`/knowledge/sources?agent_id=${agentId}`);
    return response.data;
  },
  addFAQ: async (agentId, question, answer) => {
    // Legacy endpoint (still works)
    const response = await api.post('/knowledge/faq', {
      agent_id: agentId,
      question,
      answer
    });
    return response.data;
  },
  ingestFAQ: async (agentId, name, text) => {
    // Prompt 7: New FAQ ingestion endpoint
    const response = await api.post('/knowledge/faq/ingest', {
      agent_id: agentId,
      name,
      text
    });
    return response.data;
  },
  uploadFile: async (agentId, file, name = null) => {
    // Prompt 9: File upload with optional name
    const formData = new FormData();
    formData.append('file', file);
    if (name) {
      formData.append('name', name);
    }
    const url = name 
      ? `/knowledge/upload?agent_id=${agentId}&name=${encodeURIComponent(name)}`
      : `/knowledge/upload?agent_id=${agentId}`;
    const response = await api.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  ingestSite: async (agentId, startUrl, name = null, maxPages = 25, maxDepth = 2, sameDomainOnly = true) => {
    // Prompt 11: Website ingestion
    const response = await api.post('/knowledge/site', {
      agent_id: agentId,
      start_url: startUrl,
      name: name || null,
      max_pages: maxPages,
      max_depth: maxDepth,
      same_domain_only: sameDomainOnly
    });
    return response.data;
  },
  delete: async (knowledgeId) => {
    const response = await api.delete(`/knowledge/${knowledgeId}`);
    return response.data;
  }
};

// Chat API
export const chatAPI = {
  send: async (agentId, conversationId, message, channel = 'dashboard', useRag = false) => {
    // Prompt 8: Add use_rag parameter
    const response = await api.post('/chat', {
      agent_id: agentId,
      conversation_id: conversationId,
      message,
      channel,
      use_rag: useRag
    });
    return response.data;
  }
};

// Public API (Prompt 10: Widget)
export const publicAPI = {
  getAgent: async (agentId) => {
    const response = await api.get(`/public/agents/${agentId}`);
    return response.data;
  }
};

// Widget API (Prompt 10: public endpoints)
export const widgetAPI = {
  getConfig: async (agentId) => {
    // Public widget config (no auth required)
    const response = await axios.get(`${API}/widget/${agentId}/config`);
    return response.data;
  },
  chat: async (agentId, conversationId, message) => {
    // Widget channel always uses RAG - uses main chat endpoint with widget channel
    const response = await api.post('/chat', {
      agent_id: agentId,
      conversation_id: conversationId,
      message,
      channel: 'widget',
      use_rag: true
    });
    return response.data;
  }
};

// Logs API
export const logsAPI = {
  list: async (agentId, limit = 50) => {
    const response = await api.get(`/logs/${agentId}?limit=${limit}`);
    return response.data;
  }
};

export default api;
